import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { IdVerifyModal } from '@/components/verify/IdVerifyModal';
import type { Product } from '@/components/product/ProductCard';
import {
  detectBrand, detectSize, detectPrice, detectColour,
  buildTitle, generateDescription, generateTags, generateSeo, median, garmentType, TONES,
  conditionOptions, classifyListing, conditionAxisLabel,
  type Tone, type DescFields,
} from '@/lib/aiListing';

/* ───────────────────────── Types & static data ───────────────────────── */

interface SellForm {
  category: string;
  brand: string;
  title: string;
  description: string;
  size: string;
  weight: string;
  color: string;
  colorOther?: string;
  condition: 'NEW_TAGS' | 'LIKE_NEW' | 'EXCELLENT' | 'GOOD' | 'FAIR';
  serialOrImei?: string;
  priceGBP: number;
  originalPriceGBP?: number;
  quantity: number;
  acceptsOffers: boolean;
  acceptsSwap: boolean;
  minOfferGBP?: number;
}

interface PhotoFile { id: string; file: File; url: string }

const categories = [
  { slug: 'womenswear',  label: 'Womenswear',  icon: '👗' },
  { slug: 'menswear',    label: 'Menswear',    icon: '👔' },
  { slug: 'shoes',       label: 'Shoes',       icon: '👟' },
  { slug: 'bags',        label: 'Bags',        icon: '👜' },
  { slug: 'accessories', label: 'Accessories', icon: '💍' },
  { slug: 'watches',     label: 'Watches',     icon: '⌚' },
  { slug: 'electronics', label: 'Electronics', icon: '📱' },
  { slug: 'traditional', label: 'Traditional', icon: '🥻' },
  { slug: 'kids',        label: 'Kids',        icon: '🧸' },
  { slug: 'fragrance',   label: 'Fragrance',   icon: '🌸' },
];

const colourSwatches = [
  { v: 'black', hex: '#0F0F12' }, { v: 'navy', hex: '#1D2735' }, { v: 'grey', hex: '#8A8A8A' },
  { v: 'cream', hex: '#F2EDE3', border: true }, { v: 'camel', hex: '#B07D4F' }, { v: 'white', hex: '#F7F4EE', border: true },
  { v: 'green', hex: '#3F5A4D' }, { v: 'blue', hex: '#3A5BA0' }, { v: 'plum', hex: '#5A3A52' },
  { v: 'red', hex: '#B23A48' }, { v: 'pink', hex: '#D98BA6' }, { v: 'gold', hex: '#D98A2B' },
];

const popularBrands = ['Apple', 'Acne Studios', 'Burberry', 'COS', 'Loewe', 'Max Mara', 'Mulberry', 'Nike', 'Sony', 'Rolex'];

const MIN_DESCRIPTION = 30;
const MAX_DESCRIPTION = 500;
const minPhotos = 4;

/* ───────────────────────── Component ───────────────────────── */

export default function SellNew() {
  const { register, handleSubmit, setValue, control, formState: { isSubmitting } } =
    useForm<SellForm>({
      defaultValues: { category: 'womenswear', condition: 'LIKE_NEW', acceptsOffers: true, acceptsSwap: false, color: 'black', quantity: 1 },
    });

  const values = useWatch({ control });
  const navigate = useNavigate();
  const location = useLocation();

  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [serialPhoto, setSerialPhoto] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [imeiStatus, setImeiStatus] = useState<'idle' | 'checking' | 'clean' | 'flagged'>('idle');
  const [brandQuery, setBrandQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // AI state
  const [aiPrompt, setAiPrompt] = useState('');
  const [tone, setTone] = useState<Tone>('friendly');
  const [aiBusy, setAiBusy] = useState<'' | 'all' | 'desc' | 'price' | 'seo'>('');
  const [tags, setTags] = useState<string[]>([]);
  const [seo, setSeo] = useState<{ title: string; description: string } | null>(null);
  const [priceRange, setPriceRange] = useState<{ low: number; high: number; comps: number } | null>(null);

  // Authenticity tiers (mirrors backend/src/lib/authenticity.ts).
  const requiresImei = ['electronics', 'watches'].includes(values.category ?? '');
  const requiresAuthPhotoOnly = ['bags', 'accessories', 'fragrance'].includes(values.category ?? '');
  // Every gated category needs an authenticity photo; only the IMEI tier also
  // needs a typed, checkable serial.
  const requiresAuthPhoto = requiresImei || requiresAuthPhotoOnly;
  const authPhotoHint =
    values.category === 'bags' ? 'Date code, authenticity card or original receipt'
    : values.category === 'accessories' ? 'Hallmark, stamp or authenticity card'
    : values.category === 'fragrance' ? 'Batch code or original receipt'
    : 'Photo of the serial / IMEI number';
  const price = Number(values.priceGBP ?? 0);
  const rrp = Number(values.originalPriceGBP ?? 0);
  const discount = rrp > price && price > 0 ? Math.round((1 - price / rrp) * 100) : 0;
  const descriptionLen = (values.description ?? '').length;

  /* Comparable listings for AI pricing */
  const { data: catalogue = [] } = useQuery<Product[]>({
    queryKey: ['products', 'all'],
    queryFn: async () => (await api.get('/products')).data,
    retry: false,
    staleTime: 60_000,
  });

  /* Clean up object URLs */
  useEffect(() => () => photos.forEach((p) => URL.revokeObjectURL(p.url)), [photos]);

  /* Prefill from the AI listing tool (router state, or session fallback after login) */
  useEffect(() => {
    const fromState = (location.state as any)?.prefill;
    let draft = fromState;
    if (!draft) {
      try { const s = sessionStorage.getItem('souqli_ai_draft'); if (s) draft = JSON.parse(s); } catch { /* ignore */ }
    }
    if (!draft) return;
    (['category', 'brand', 'title', 'description', 'size', 'color', 'condition', 'priceGBP', 'originalPriceGBP'] as const)
      .forEach((k) => { const v = (draft as any)[k]; if (v !== undefined && v !== null && v !== '') setValue(k as any, v); });
    if (Array.isArray(draft.photos) && draft.photos.length) {
      setPhotos(draft.photos.filter((f: any) => f instanceof File)
        .map((file: File) => ({ id: crypto.randomUUID(), file, url: URL.createObjectURL(file) })).slice(0, 10));
    }
    sessionStorage.removeItem('souqli_ai_draft');
    if (fromState) window.history.replaceState({}, '');
    toast.success('Draft loaded from the AI listing tool - review, add photos and submit.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ───── Photo handling ───── */
  const addFiles = useCallback((files: FileList | File[]) => {
    const incoming = Array.from(files).filter((f) => f.type.startsWith('image/') && f.size <= 12 * 1024 * 1024);
    if (incoming.length === 0) return toast.error('Drop JPG/PNG/HEIC up to 12 MB each.');
    setPhotos((prev) => [...prev, ...incoming.map((file) => ({ id: crypto.randomUUID(), file, url: URL.createObjectURL(file) }))].slice(0, 10));
    // Quality nudge: originals are uploaded and stored at full resolution, but
    // warn (don't block) on a low-res photo so listings always look sharp.
    incoming.forEach((file) => {
      const objUrl = URL.createObjectURL(file);
      const probe = new Image();
      probe.onload = () => {
        if (Math.min(probe.naturalWidth, probe.naturalHeight) < 800) {
          toast(`"${file.name}" looks low-resolution (${probe.naturalWidth}x${probe.naturalHeight}). A sharper photo (800px or more) will sell better.`, { icon: '⚠️', duration: 5000 });
        }
        URL.revokeObjectURL(objUrl);
      };
      probe.onerror = () => URL.revokeObjectURL(objUrl);
      probe.src = objUrl;
    });
  }, []);
  const removePhoto = (id: string) => setPhotos((prev) => { const p = prev.find((x) => x.id === id); if (p) URL.revokeObjectURL(p.url); return prev.filter((x) => x.id !== id); });
  const reorderPhoto = (from: number, to: number) => setPhotos((prev) => { const next = [...prev]; const [m] = next.splice(from, 1); next.splice(to, 0, m); return next; });

  /* ───── IMEI verification ───── */
  const verifyImei = async () => {
    const imei = (values.serialOrImei ?? '').replace(/\s+/g, '');
    if (imei.length < 8) return toast.error('Enter the full IMEI (14-15 digits).');
    setImeiStatus('checking');
    try {
      const { data } = await api.post('/listings/check-imei', { imei });
      const flagged = data?.result === 'FLAGGED' || data?.ok === false;
      setImeiStatus(flagged ? 'flagged' : 'clean');
      if (flagged) toast.error('IMEI is flagged on the stolen-goods register.');
      else toast.success('IMEI/serial checked · clear to list.');
    } catch (e: any) {
      setImeiStatus('idle');
      toast.error(e?.response?.data?.message ?? 'Verification service unavailable. Try again shortly.');
    }
  };

  /* ───── AI helpers ───── */
  const currentFields = (): DescFields => {
    const garment = garmentType({ title: values.title ?? '', brand: values.brand, category: values.category });
    return {
      brand: values.brand, title: values.title, category: values.category,
      colour: detectColour(`${values.title ?? ''} ${values.color ?? ''}`) ?? values.color,
      condition: (values.condition ?? 'LIKE_NEW') as SellForm['condition'],
      size: values.size, notes: values.description || `${values.title ?? ''} ${values.brand ?? ''}`, garment,
    };
  };

  function suggestPrice(cat?: string, garm?: string, brand?: string) {
    const category = cat ?? values.category ?? '';
    const garment = garm ?? garmentType({ title: values.title ?? '', brand: values.brand });
    const b = brand ?? values.brand;
    const pool = catalogue.filter((c) => (c as any).category === category);
    const sameBrand = b ? pool.filter((c) => (c.brand ?? '').toLowerCase().includes(String(b).toLowerCase().split(' ')[0])) : [];
    const sameType = pool.filter((c) => garmentType(c) === garment);
    const chosen = sameBrand.length >= 2 ? sameBrand : sameType.length >= 2 ? sameType : pool;
    const mid = median(chosen.map((c) => c.priceGBP).filter((n) => n > 0));
    if (!mid) { toast('Not enough comparable items to price yet.', { icon: 'ℹ️' }); return; }
    const low = Math.max(1, Math.round((mid * 0.85) / 5) * 5);
    const high = Math.round((mid * 1.15) / 5) * 5;
    setPriceRange({ low, high, comps: chosen.length });
    if (!values.priceGBP) setValue('priceGBP', Math.round(mid / 5) * 5, { shouldValidate: true });
  }

  function aiGenerateAll() {
    const text = (aiPrompt || `${values.title ?? ''} ${values.brand ?? ''} ${values.description ?? ''}`).trim();
    if (text.length < 8) { toast.error('Describe the item first - a sentence is plenty.'); return; }
    setAiBusy('all');
    try {
      const brand = detectBrand(text);
      // One smart agent pass decides category, condition and product family
      // together, so a sealed phone reads as "Sealed", a pre-owned watch as
      // "Pre-owned" and an 80%-full scent as "90%+ full" - not just "worn".
      const { category, garment, condition, family, sealed, fillPercent } = classifyListing(text);
      const colour = detectColour(text);
      const size = detectSize(text, garment);
      const typedPrice = detectPrice(text);
      if (brand) setValue('brand', brand);
      setValue('category', category);
      setValue('condition', condition);
      if (colour) setValue('color', colour);
      if (size) setValue('size', size);
      const title = buildTitle(text, brand);
      setValue('title', title, { shouldValidate: true });
      const fields: DescFields = { brand, title, category, colour, condition, size, notes: text, garment };
      setValue('description', generateDescription(fields, tone), { shouldValidate: true });
      setTags(generateTags(fields));
      setSeo(generateSeo(fields));
      if (typedPrice) setValue('priceGBP', typedPrice, { shouldValidate: true });
      else suggestPrice(category, garment, brand);
      const grade = conditionOptions(category, garment).find((c) => c.v === condition)?.label ?? condition;
      const extra = family === 'fragrance' && fillPercent !== undefined ? ` (${fillPercent}% full)` : sealed ? ' (sealed)' : '';
      toast.success(`Sorted into ${category} · ${grade}${extra}. Review and tweak below.`);
    } finally { setAiBusy(''); }
  }

  function aiWriteDescription(t?: Tone) {
    const useTone = t ?? tone;
    if (t) setTone(t);
    if (!values.title && !values.brand && !values.description) { toast.error('Add a title/brand first, or use the AI assistant.'); return; }
    setAiBusy('desc');
    try { setValue('description', generateDescription(currentFields(), useTone), { shouldValidate: true }); }
    finally { setAiBusy(''); }
  }

  function aiTagsSeo() {
    setAiBusy('seo');
    try { const f = currentFields(); setTags(generateTags(f)); setSeo(generateSeo(f)); toast.success('Tags & SEO generated.'); }
    finally { setAiBusy(''); }
  }

  /* ───── Submit ───── */
  // Shrink photos in the browser before upload: large multi-photo listings can
  // otherwise exceed the server/proxy request-size limit and fail to submit with
  // no useful error. Falls back to the original file if the image can't be
  // processed (e.g. HEIC on a browser that can't decode it).
  async function compressImage(file: File, maxEdge = 2000, quality = 0.82): Promise<File> {
    if (!file.type.startsWith('image/')) return file;
    try {
      let width = 0, height = 0;
      let source: CanvasImageSource | null = null;
      let bmp: ImageBitmap | null = null;
      try {
        bmp = await createImageBitmap(file);
        width = bmp.width; height = bmp.height; source = bmp;
      } catch {
        const url = URL.createObjectURL(file);
        try {
          const img = await new Promise<HTMLImageElement>((res, rej) => {
            const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = url;
          });
          width = img.naturalWidth; height = img.naturalHeight; source = img;
        } finally { URL.revokeObjectURL(url); }
      }
      if (!width || !height || !source) return file;
      const scale = Math.min(1, maxEdge / Math.max(width, height));
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(source, 0, 0, w, h);
      bmp?.close?.();
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', quality));
      if (!blob || blob.size >= file.size) return file;
      const base = file.name.replace(/\.[^.]+$/, '') || 'photo';
      return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
    } catch {
      return file;
    }
  }

  async function onSubmit(v: SellForm) {
    if (!v.category) return toast.error('Choose a category.');
    if (!v.brand) return toast.error('Add the brand.');
    if (!v.title) return toast.error('Add a title.');
    if (!v.description || v.description.length < MIN_DESCRIPTION) return toast.error(`Description needs ${MIN_DESCRIPTION}+ characters.`);
    if (!v.priceGBP || v.priceGBP < 1) return toast.error('Set your asking price.');
    if (photos.length < minPhotos) return toast.error(`Add at least ${minPhotos} photos.`);
    if (!v.size || !v.size.trim()) return toast.error('Add the size (or "One size" if it doesn\'t apply).');
    if (!v.weight || !v.weight.trim() || !/\d/.test(v.weight)) return toast.error('Add the parcel weight, e.g. 0.5 kg.');
    if (requiresImei && imeiStatus !== 'clean') return toast.error('Verify the IMEI first.');
    if (requiresAuthPhoto && !serialPhoto) return toast.error('Add an authenticity photo for this category.');
    if (v.color === 'other') {
      const custom = (v.colorOther ?? '').trim();
      if (!custom) return toast.error('Type the colour you want, or pick one of the swatches.');
      v.color = custom;
    }
    delete (v as any).colorOther;
    try {
      const fd = new FormData();
      Object.entries(v).forEach(([k, val]) => {
        // Skip empty and NaN values (unfilled number inputs give NaN via valueAsNumber).
        if (val === undefined || val === null || val === '') return;
        if (typeof val === 'number' && Number.isNaN(val)) return;
        fd.append(k, String(val));
      });
      const compressedPhotos = await Promise.all(photos.map((p) => compressImage(p.file)));
      compressedPhotos.forEach((f) => fd.append('photos', f));
      if (serialPhoto) fd.append('serialPhoto', await compressImage(serialPhoto, 2400, 0.9));
      if (tags.length) fd.append('tags', tags.join(','));
      const { data } = await api.post('/listings', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Listing submitted - under review. It usually goes live within 24-48 hours.', { duration: 7000 });
      if (data.aiEnrichmentSkipped === 'quota') {
        toast('Listed without AI styling - you\'ve used your free AI enrichments this month. Souqli Pro gives you more.', { icon: 'ℹ️', duration: 8000 });
      }
      navigate('/dashboard/listed', { state: { code: data.code, id: data.id, title: v.title } });
    } catch (e: any) {
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.message;
      if (status === 413) {
        toast.error('Your photos are too large to upload together. Remove one or use smaller images and try again.');
      } else if (serverMsg) {
        toast.error(serverMsg);
      } else if (!e?.response) {
        toast.error('Could not reach the server - check your connection and try again.');
      } else {
        toast.error(`Could not submit (error ${status ?? 'unknown'}). Please try again.`);
      }
    }
  }

  /* ───── Live checklist ───── */
  const checklist = useMemo(() => [
    { ok: photos.length >= minPhotos, label: `${photos.length}/${minPhotos} photos` },
    { ok: Boolean(values.category), label: 'Category chosen' },
    { ok: Boolean(values.brand && values.title), label: 'Brand & title' },
    { ok: Boolean(values.size && values.size.trim()), label: values.size ? `Size ${values.size}` : 'Size required' },
    { ok: Boolean(values.weight && values.weight.trim()), label: values.weight ? `Weight ${values.weight}` : 'Weight required' },
    { ok: descriptionLen >= MIN_DESCRIPTION, label: `Description (${descriptionLen} chars)` },
    { ok: !requiresImei || imeiStatus === 'clean', label: requiresImei ? 'IMEI verified' : 'No serial needed' },
    ...(requiresAuthPhoto ? [{ ok: Boolean(serialPhoto), label: serialPhoto ? 'Authenticity photo added' : 'Authenticity photo required' }] : []),
    { ok: Boolean(values.priceGBP && Number(values.priceGBP) > 0), label: values.priceGBP ? `Price £${values.priceGBP}` : 'Price set' },
  ], [photos.length, values, requiresImei, requiresAuthPhoto, serialPhoto, imeiStatus, descriptionLen]);

  const filteredBrands = brandQuery ? popularBrands.filter((b) => b.toLowerCase().includes(brandQuery.toLowerCase())) : popularBrands.slice(0, 6);
  const descChecks = {
    length: descriptionLen >= 80,
    flaws: /flaw|scratch|mark|stain|wear|crack|fade/i.test(values.description ?? ''),
    extras: /box|tag|receipt|cable|case|charger|accessor/i.test(values.description ?? ''),
  };

  /* ───── Seller verification gate ───── */
  const { data: stripeStatus } = useQuery<{ required: boolean; chargesEnabled?: boolean; payoutsEnabled: boolean; detailsSubmitted?: boolean }>({
    queryKey: ['connect-status'],
    queryFn: async () => (await api.get('/connect/status')).data,
    retry: false,
  });
  const [verifying, setVerifying] = useState(false);
  // BOTH gates are hard requirements to list, matching the backend exactly:
  // (1) an APPROVED identity (Didit) AND (2) Stripe payouts fully enabled.
  // We gate on payoutsEnabled - NOT merely detailsSubmitted - so a seller is
  // never allowed into the editor only to be rejected at publish by the
  // server's STRIPE_ONBOARDING_REQUIRED check, which itself requires
  // stripePayoutsEnabled. Keeping the two sides identical is the whole point:
  // one of the two is never enough, both must be complete.
  const stripeComplete = !!stripeStatus?.payoutsEnabled;
  const needsVerification = !!stripeStatus?.required && !stripeComplete;
  // Listing is blocked until payouts are live, so the old "list now, finish
  // payouts later" path no longer applies.
  const payoutIncomplete = false;

  // Identity gate: a verified identity (Didit) is required to list, alongside
  // payout onboarding. Reuses the cached ['profile'] query.
  const { data: sellerProfile } = useQuery<{ idVerification?: 'PENDING' | 'APPROVED' | 'REJECTED' }>({
    queryKey: ['profile'],
    queryFn: async () => (await api.get('/me/profile')).data,
    retry: false,
  });
  const idVerified = sellerProfile?.idVerification === 'APPROVED';
  const [idModal, setIdModal] = useState(false);

  async function startVerification() {
    // Open Stripe's hosted onboarding in a new tab so the Souqli page stays open behind it.
    const stripeTab = window.open('about:blank', '_blank');
    setVerifying(true);
    try {
      const { data } = await api.post('/connect/onboard');
      if (data?.url) {
        if (stripeTab) { stripeTab.location.href = data.url; }
        else { window.location.href = data.url; }
        toast.success('Stripe opened in a new tab. Come back here when you\u2019re done.');
        return;
      }
      if (stripeTab) stripeTab.close();
      toast.error('Could not start verification. Try again.');
    } catch (e: any) {
      if (stripeTab) stripeTab.close();
      toast.error(e?.response?.data?.message ?? 'Verification is unavailable right now.');
    } finally { setVerifying(false); }
  }

  /* ───────────────────────── Verification gate ───────────────────────── */
  // Both must be complete before listing: a verified identity AND connected
  // payouts. Shown as a checklist so a seller sees exactly what's left.
  if (!idVerified || needsVerification) {
    return (
      <div className="max-w-[640px] mx-auto">
        <div className="section-dark relative overflow-hidden rounded-luxe bg-ink text-cream p-8 md:p-10">
          <div className="absolute inset-0 dotgrid-light opacity-20 bg-[length:18px_18px]" />
          <div className="relative">
            <div className="text-4xl mb-4 text-center">🔐</div>
            <div className="stamp text-gold text-[10px] text-center">VERIFY TO SELL</div>
            <h1 className="display text-3xl md:text-4xl mt-2 leading-tight text-center">Two steps before you can list</h1>
            <p className="text-cream/70 text-sm mt-3 max-w-[48ch] mx-auto leading-relaxed text-center">
              Souqli holds buyer payments securely until the buyer confirms receipt. To keep the marketplace safe, verify your identity and connect your payouts before listing; it only takes a minute.
            </p>
            <div className="mt-7 space-y-3 text-left">
              <div className="flex items-center gap-4 rounded-luxe border border-cream/15 bg-cream/5 px-4 py-3.5">
                <span className={`w-9 h-9 rounded-full grid place-items-center text-lg shrink-0 ${idVerified ? 'bg-ok text-purewhite' : 'bg-cream/10 text-cream/70'}`}>{idVerified ? '✓' : '1'}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">Verify your identity</div>
                  <div className="text-[12px] text-cream/60">{idVerified ? 'Done. Your identity is verified.' : 'A quick photo-ID and selfie check via our verification partner.'}</div>
                </div>
                {!idVerified && <button onClick={() => setIdModal(true)} className="btn btn-sm btn-gold shrink-0">Verify ID →</button>}
              </div>
              <div className="flex items-center gap-4 rounded-luxe border border-cream/15 bg-cream/5 px-4 py-3.5">
                <span className={`w-9 h-9 rounded-full grid place-items-center text-lg shrink-0 ${!needsVerification ? 'bg-ok text-purewhite' : 'bg-cream/10 text-cream/70'}`}>{!needsVerification ? '✓' : '2'}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">Connect your payouts</div>
                  <div className="text-[12px] text-cream/60">{!needsVerification ? 'Done. Stripe connected.' : 'Connect Stripe so your sales can be paid out to your bank.'}</div>
                </div>
                {needsVerification && <button onClick={startVerification} disabled={verifying} className="btn btn-sm btn-gold shrink-0">{verifying ? 'Opening Stripe…' : 'Connect →'}</button>}
              </div>
            </div>
          </div>
        </div>
        <IdVerifyModal open={idModal} onClose={() => setIdModal(false)} />
      </div>
    );
  }

  /* ───────────────────────── Shopify-style editor ───────────────────────── */
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 -mx-4 sm:mx-0 px-4 sm:px-0 py-3 bg-cream/92 backdrop-blur border-b border-warmline flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button type="button" onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-full grid place-items-center hover:bg-cream-deep text-mute shrink-0" aria-label="Back">←</button>
          <div className="min-w-0">
            <h1 className="display text-xl leading-none truncate">{values.title || 'Add product'}</h1>
            <p className="text-[11px] text-mute mt-0.5">Draft · not yet submitted</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={() => toast.success('Saved as draft.')} className="btn btn-ghost btn-sm text-mute hidden sm:inline-flex">Save draft</button>
          <button type="submit" disabled={isSubmitting} className="btn btn-primary btn-sm">
            {isSubmitting ? 'Publishing…' : '✓ Publish listing'}
          </button>
        </div>
      </div>

      {payoutIncomplete && (
        <div className="mt-4 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3 flex items-start gap-3">
          <span className="text-lg leading-none">⏳</span>
          <p className="text-[13px] text-ink leading-snug">
            You can list now, but your payout setup on Stripe isn’t finished yet - you’ll need to
            complete it before your held earnings can be released.{' '}
            <button type="button" onClick={startVerification} disabled={verifying} className="underline font-medium">
              {verifying ? 'Opening Stripe…' : 'Finish payout setup'}
            </button>
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {/* ─────────── MAIN COLUMN ─────────── */}
        <div className="lg:col-span-2 space-y-6">

          {/* Title + description */}
          <Card>
            <label className="label">Title</label>
            <input className="input" placeholder="e.g. Max Mara Camel Cashmere Coat" maxLength={80} {...register('title', { required: true })} />
            <div className="flex items-center justify-between mt-1.5">
              <p className="stamp text-mute text-[9px]">CLEAR &amp; SEARCHABLE</p>
              <p className="stamp text-mute text-[9px]">{(values.title ?? '').length} / 80</p>
            </div>

            <div className="flex items-center justify-between mt-5 mb-2 gap-2 flex-wrap">
              <label className="label mb-0">Description</label>
              {/* AI toolbar */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <select value={tone} onChange={(e) => setTone(e.target.value as Tone)} className="text-[11px] border border-warmline rounded-luxe px-2 py-1 bg-white text-ink">
                  {TONES.map((t) => <option key={t.v} value={t.v}>{t.icon} {t.label}</option>)}
                </select>
                <button type="button" onClick={() => aiWriteDescription()} disabled={aiBusy !== ''} className="text-[11px] font-medium px-2.5 py-1 rounded-luxe bg-gold/15 text-gold-dark hover:bg-gold/25 transition">
                  {aiBusy === 'desc' ? 'Writing…' : '✨ Write with AI'}
                </button>
                <button type="button" onClick={() => aiWriteDescription('minimal')} disabled={aiBusy !== ''} className="text-[11px] px-2.5 py-1 rounded-luxe border border-warmline text-mute hover:text-ink hover:border-gold/50 transition">Shorten</button>
              </div>
            </div>
            <textarea className="input min-h-[150px]" placeholder="Describe the piece, or tap ✨ Write with AI to draft it for you." maxLength={MAX_DESCRIPTION} {...register('description', { required: true, minLength: MIN_DESCRIPTION })} />
            <div className="mt-2 h-1 bg-cream-deep rounded-full overflow-hidden relative">
              <div className={`h-full transition-all ${descriptionLen >= MIN_DESCRIPTION ? 'bg-ok' : 'bg-warn'}`} style={{ width: `${Math.min(100, (descriptionLen / MAX_DESCRIPTION) * 100)}%` }} />
            </div>
            <div className="flex items-center justify-between mt-2 text-[10px]">
              <div className="flex flex-wrap gap-3">
                <Hint ok={descChecks.length} label="80+ chars" />
                <Hint ok={descChecks.flaws} label="Mentions condition" />
                <Hint ok={descChecks.extras} label="Lists extras" />
              </div>
              <span className={`stamp ${descriptionLen >= MIN_DESCRIPTION ? 'text-ok' : 'text-warn'}`}>{descriptionLen} / {MAX_DESCRIPTION}</span>
            </div>
          </Card>

          {/* Media */}
          <Card>
            <CardTitle hint={`Add ${minPhotos}+ photos. First is the cover. Drag to reorder.`}>Media</CardTitle>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-luxe p-6 text-center bg-cream-deep/50 cursor-pointer transition ${dragOver ? 'border-gold bg-gold/5' : 'border-warmline hover:border-gold'}`}
            >
              <div className="text-3xl mb-1">📸</div>
              <div className="text-sm font-medium">Drop photos or click to upload</div>
              <div className="stamp text-mute text-[9px] mt-1">JPG · HEIC · PNG · MAX 12 MB EACH</div>
              <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => e.target.files && addFiles(e.target.files)} />
            </div>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5 mt-4">
                {photos.map((p, i) => (
                  <div key={p.id} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); if (dragIdx !== null && dragIdx !== i) reorderPhoto(dragIdx, i); setDragIdx(null); }}
                    className={`relative aspect-square notched-sm overflow-hidden cursor-move ${i === 0 ? 'ring-2 ring-gold' : 'border border-warmline'}`}>
                    <img src={p.url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                    <span className={`absolute top-1 left-1 stamp text-[8px] px-1.5 py-0.5 rounded ${i === 0 ? 'bg-gold text-ink' : 'bg-ink/80 text-cream'}`}>{i === 0 ? 'COVER' : String(i + 1).padStart(2, '0')}</span>
                    <button type="button" aria-label="Remove" onClick={(e) => { e.stopPropagation(); removePhoto(p.id); }} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-ink/80 text-cream text-[10px] grid place-items-center hover:bg-bad">×</button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Pricing */}
          <Card>
            <CardTitle>Pricing</CardTitle>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Your price</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink font-bold display text-2xl leading-none pointer-events-none">£</span>
                  <input className="input pl-9 text-lg font-semibold" type="number" min={1} step="0.01" placeholder="0" {...register('priceGBP', { required: true, valueAsNumber: true })} />
                </div>
                <button type="button" onClick={() => suggestPrice()} className="mt-1.5 text-[11px] font-medium text-gold-dark hover:underline">✨ Suggest a price from comparable sales</button>
                {priceRange && (
                  <p className="stamp text-mute text-[9px] mt-1">⌖ SUGGESTED £{priceRange.low}-£{priceRange.high} · {priceRange.comps} COMPARABLE</p>
                )}
              </div>
              <div>
                <label className="label">Compare-at / RRP <span className="text-mute font-normal lowercase">- optional</span></label>
                <div className="relative">
                  <span className="absolute left-4 top-3 text-mute display text-base">£</span>
                  <input className="input pl-8 text-lg" type="number" min={1} step="0.01" placeholder="-" {...register('originalPriceGBP', { valueAsNumber: true })} />
                </div>
                <p className="stamp text-mute text-[9px] mt-1">{discount ? `SHOWS A "−${discount}%" BADGE` : 'ADDS A DISCOUNT BADGE'}</p>
              </div>
            </div>

            <div className="mt-4">
              <label className="label">Quantity available</label>
              <input className="input text-lg w-32" type="number" min={1} step={1} {...register('quantity', { valueAsNumber: true, min: 1 })} />
              <p className="stamp text-mute text-[9px] mt-1">HOW MANY IDENTICAL UNITS YOU'RE SELLING (e.g. 2 PAIRS OF THE SAME SHOE). LEAVE AT 1 FOR A ONE-OFF.</p>
            </div>

            <div className="mt-4 space-y-2.5">
              <label className="flex items-start gap-3 p-3 border border-warmline rounded-luxe cursor-pointer hover:border-gold transition">
                <input type="checkbox" className="mt-1 accent-gold" {...register('acceptsOffers')} />
                <div className="flex-1"><div className="text-sm font-medium">Allow offers</div><div className="stamp text-mute text-[9px] mt-0.5">ACCEPT · DECLINE · COUNTER</div></div>
                <span className="pill pill-gold text-[9px]">+47% INTEREST</span>
              </label>
              {values.acceptsOffers && (
                <div className="relative">
                  <span className="absolute left-4 top-3 text-mute display text-base">£</span>
                  <input className="input pl-8" type="number" min={1} step="0.01" placeholder={price ? `Min offer (default ${Math.floor(price * 0.7)})` : 'Minimum offer'} {...register('minOfferGBP', { valueAsNumber: true })} />
                </div>
              )}
              <label className="flex items-start gap-3 p-3 border-2 border-clay/30 bg-clay/5 rounded-luxe cursor-pointer hover:border-clay transition">
                <input type="checkbox" className="mt-1 accent-gold" {...register('acceptsSwap')} />
                <div className="flex-1"><div className="text-sm font-medium">Open to swaps (devices)</div><div className="stamp text-mute text-[9px] mt-0.5">TRADE + OPTIONAL CASH TOP-UP</div></div>
              </label>
            </div>
          </Card>

          {/* Item details */}
          <Card>
            <CardTitle hint="Buyers filter by these. AI can fill them from your description.">Item details</CardTitle>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Brand</label>
                <input className="input" placeholder="Start typing - we suggest" {...register('brand', { required: true, onChange: (e) => setBrandQuery(e.target.value) })} />
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {filteredBrands.slice(0, 6).map((b) => (
                    <button key={b} type="button" onClick={() => { setValue('brand', b, { shouldValidate: true }); setBrandQuery(''); }} className="stamp text-[9px] bg-cream-deep hover:bg-gold/20 text-mute hover:text-gold-dark px-2 py-1 rounded transition">▾ {b.toUpperCase()}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">Category</label>
                <select className="input" {...register('category', { required: true })}>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.icon} {c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Size / storage / volume <span className="pill pill-clay text-[9px] ml-1">REQUIRED</span></label>
                <input className="input" placeholder="UK 10 · 256GB · M · 100ml · One size" {...register('size', { required: true })} />
                <p className="text-[11px] text-mute mt-1">Auto-filled from your description when possible. Use "One size" if it doesn't apply.</p>
              </div>
              <div>
                <label className="label">Colour</label>
                <div className="flex gap-2 flex-wrap">
                  {colourSwatches.map((c) => (
                    <label key={c.v} className="cursor-pointer" title={c.v}>
                      <input type="radio" value={c.v} {...register('color')} className="peer sr-only" />
                      <span className={`block w-8 h-8 rounded-full transition peer-checked:ring-2 peer-checked:ring-gold peer-checked:ring-offset-2 peer-checked:ring-offset-cream ${c.border ? 'border border-warmline' : ''}`} style={{ background: c.hex }} />
                    </label>
                  ))}
                  <label className="cursor-pointer" title="Other colour">
                    <input type="radio" value="other" {...register('color')} className="peer sr-only" />
                    <span className="flex items-center justify-center w-8 h-8 rounded-full border border-dashed border-mute text-mute text-[15px] leading-none transition peer-checked:ring-2 peer-checked:ring-gold peer-checked:ring-offset-2 peer-checked:ring-offset-cream peer-checked:text-gold peer-checked:border-gold">+</span>
                  </label>
                </div>
                {values.color === 'other' && (
                  <input className="input mt-2" maxLength={30} placeholder="Type the colour - e.g. burgundy, olive, multicolour" {...register('colorOther')} />
                )}
              </div>
            </div>

            <label className="label mt-5">Condition <span className="text-mute font-normal lowercase">- {conditionAxisLabel(values.category, garmentType({ title: values.title, brand: values.brand, category: values.category }))}</span></label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {conditionOptions(values.category, garmentType({ title: values.title, brand: values.brand, category: values.category })).map((c) => (
                <label key={c.v} className="cursor-pointer">
                  <input type="radio" value={c.v} {...register('condition', { required: true })} className="peer sr-only" />
                  <div className="border border-warmline rounded-luxe p-2.5 peer-checked:border-2 peer-checked:border-gold peer-checked:bg-gold/8 hover:border-gold transition">
                    <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i < c.dots ? 'bg-gold' : 'bg-warmline'}`} />)}</div>
                    <div className="text-[13px] font-medium mt-1.5">{c.label}</div>
                    <div className="stamp text-mute text-[8px] mt-0.5">{c.body}</div>
                  </div>
                </label>
              ))}
            </div>

            <div className="mt-5">
              <label className="label">Weight <span className="pill pill-clay text-[9px] ml-1">REQUIRED</span></label>
              <input className="input" placeholder="e.g. 0.5 kg" {...register('weight', { required: true })} />
              <p className="text-[11px] text-mute mt-1">Used to calculate the delivery price buyers see at checkout.</p>
            </div>
          </Card>

          {/* Authenticity */}
          <Card>
            <CardTitle hint="Protects buyers and reduces disputes.">Authenticity &amp; serial</CardTitle>
            {requiresImei && (
              <div className="mb-4">
                <label className="label">Serial / IMEI <span className="pill pill-clay text-[9px] ml-1">REQUIRED</span></label>
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-stretch">
                  <input className="input sm:col-span-8 mono" placeholder="15-digit IMEI" {...register('serialOrImei', { required: requiresImei, minLength: 8 })} />
                  <button type="button" onClick={verifyImei} disabled={imeiStatus === 'checking'} className="btn btn-sm sm:col-span-4" style={{ background: imeiStatus === 'clean' ? '#4A7C59' : imeiStatus === 'flagged' ? '#B23A48' : '#5C7A6B', color: '#FAF7F2' }}>
                    {imeiStatus === 'checking' ? 'Checking…' : imeiStatus === 'clean' ? '✓ Verified' : imeiStatus === 'flagged' ? '⚠ Flagged' : 'Verify now'}
                  </button>
                </div>
                {imeiStatus === 'clean' && <p className="text-[11px] text-ok mt-2">IMEI/serial clear · safe to list.</p>}
                {imeiStatus === 'flagged' && <p className="text-[11px] text-bad mt-2">This serial is flagged. Listing cannot proceed.</p>}
              </div>
            )}
            {requiresAuthPhoto && (
              <label className="label">
                Authenticity photo
                <span className={`pill text-[9px] ml-1 ${serialPhoto ? 'pill-ok' : 'pill-clay'}`}>{serialPhoto ? 'ADDED' : 'REQUIRED'}</span>
              </label>
            )}
            <label className={`flex items-center gap-4 p-4 border-2 border-dashed rounded-luxe cursor-pointer transition ${requiresAuthPhoto && !serialPhoto ? 'border-clay hover:border-clay' : 'border-warmline hover:border-gold'}`}>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setSerialPhoto(e.target.files?.[0] ?? null)} />
              <span className="text-2xl">🔍</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{serialPhoto ? serialPhoto.name : 'Upload serial / authenticity photo'}</div>
                <div className="text-[11px] text-mute mt-0.5">
                  {serialPhoto
                    ? 'Tap to replace'
                    : requiresAuthPhoto
                      ? `${authPhotoHint} · admin-only · never shown publicly`
                      : 'Admin-only · never shown publicly · optional'}
                </div>
              </div>
              {serialPhoto && <span className="pill pill-ok text-[9px] shrink-0">Added</span>}
            </label>
            {requiresAuthPhoto && (
              <p className="text-[11px] text-mute mt-2">
                Checked before your item goes live. Listings in this category are not approved without it.
              </p>
            )}
          </Card>

          {/* SEO */}
          <Card>
            <CardTitle hint="How your listing appears in search results.">Search engine listing</CardTitle>
            <button type="button" onClick={aiTagsSeo} disabled={aiBusy !== ''} className="text-[11px] font-medium px-3 py-1.5 rounded-luxe bg-gold/15 text-gold-dark hover:bg-gold/25 transition">
              {aiBusy === 'seo' ? 'Generating…' : '✨ Generate SEO & tags'}
            </button>
            {seo ? (
              <div className="mt-4 rounded-luxe border border-warmline p-4">
                <div className="text-[15px] text-[#1a0dab] leading-snug">{seo.title}</div>
                <div className="text-[12px] text-[#006621] mt-0.5">souqli.com › {values.category ?? 'shop'}</div>
                <div className="text-[12px] text-mute mt-1 leading-snug">{seo.description}</div>
              </div>
            ) : (
              <p className="text-[12px] text-mute mt-3">Tap generate to preview your Google-style listing and keyword tags.</p>
            )}
            {tags.length > 0 && (
              <div className="mt-4">
                <div className="stamp text-gold-dark text-[9px] mb-2">TAGS</div>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] bg-cream-deep border border-warmline rounded-full text-mute">
                      {t}
                      <button type="button" onClick={() => setTags((prev) => prev.filter((x) => x !== t))} className="text-mute hover:text-bad">×</button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* ─────────── SIDEBAR ─────────── */}
        <div className="space-y-6">

          {/* AI assistant */}
          <div className="section-dark rounded-luxe p-5 text-cream relative overflow-hidden shadow-frame" style={{ background: 'linear-gradient(135deg,#1a1208,#2c1e08 60%,#0F0F12)' }}>
            <div className="absolute inset-0 dotgrid-light bg-[length:16px_16px] opacity-20" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">✨</span>
                <h2 className="text-sm font-semibold">AI listing assistant</h2>
              </div>
              <p className="text-cream/60 text-[12px] mb-3 leading-relaxed">Describe your item in a sentence. I'll fill the title, category, condition, colour, description, tags and a price.</p>
              <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="e.g. Max Mara camel cashmere coat, size M, worn twice, comes with belt. £320" className="w-full min-h-[90px] rounded-luxe bg-cream/10 border border-cream/20 text-cream placeholder:text-cream/40 text-[13px] p-3 focus:outline-none focus:border-gold" maxLength={400} />
              <button type="button" onClick={aiGenerateAll} disabled={aiBusy !== ''} className="btn btn-gold w-full mt-3 text-sm">
                {aiBusy === 'all' ? 'Generating…' : '✨ Generate full listing'}
              </button>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button type="button" onClick={() => aiWriteDescription()} disabled={aiBusy !== ''} className="text-[11px] py-1.5 rounded-luxe bg-cream/10 hover:bg-cream/20 text-cream/90 transition">Rewrite copy</button>
                <button type="button" onClick={() => suggestPrice()} disabled={aiBusy !== ''} className="text-[11px] py-1.5 rounded-luxe bg-cream/10 hover:bg-cream/20 text-cream/90 transition">Suggest price</button>
              </div>
            </div>
          </div>

          {/* Publish */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold">Status</h2>
              <span className="pill bg-cream-deep text-mute text-[9px]">DRAFT</span>
            </div>
            <button type="submit" disabled={isSubmitting} className="btn btn-primary w-full">
              {isSubmitting ? 'Publishing…' : '✓ Publish listing'}
            </button>
            <p className="text-[11px] text-mute mt-2 leading-snug">Our team verifies within 4 hours. It goes live once approved; you're paid when the buyer collects.</p>
          </Card>

          {/* Live preview */}
          <Card>
            <CardTitle>Live preview</CardTitle>
            <div className="aspect-[4/5] notched-sm relative overflow-hidden bg-gradient-to-br from-ink-700 to-ink">
              {photos[0]
                ? <img src={photos[0].url} alt="Cover" className="w-full h-full object-cover" />
                : <div className="absolute inset-0 grid place-items-center text-gold/40 display text-5xl italic">{(values.brand?.[0] ?? values.title?.[0] ?? 'S').toUpperCase()}</div>}
              {discount > 0 && <span className="absolute bottom-2 left-2 bg-black text-purewhite stamp px-2 py-0.5 rounded text-[9px]">−{discount}%</span>}
            </div>
            <div className="mt-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="stamp text-mute text-[9px]">{(values.brand ?? '-').toUpperCase()}</div>
                <div className="text-[13px] truncate">{values.title || 'Your title here'}</div>
              </div>
              <div className="text-right">
                <div className="display text-lg leading-none">£{price || 0}</div>
                {rrp > price && rrp > 0 && <div className="text-[10px] text-mute line-through">£{rrp}</div>}
              </div>
            </div>
          </Card>

          {/* Checklist */}
          <Card>
            <CardTitle>Before you publish</CardTitle>
            <ul className="space-y-2 text-[12px]">
              {checklist.map((item, i) => (
                <li key={i} className={`flex items-center gap-2 ${item.ok ? '' : 'text-mute'}`}>
                  <span className={`w-4 h-4 rounded-full grid place-items-center text-[10px] ${item.ok ? 'bg-ok text-cream' : 'border-2 border-warmline'}`}>{item.ok ? '✓' : ''}</span>
                  {item.label}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </form>
  );
}

/* ───────────────────────── Helpers ───────────────────────── */

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <section className={`bg-white border border-warmline rounded-luxe shadow-luxe p-5 ${className}`}>{children}</section>;
}

function CardTitle({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-sm font-semibold text-ink">{children}</h2>
      {hint && <p className="text-[12px] text-mute mt-0.5 leading-snug">{hint}</p>}
    </div>
  );
}

function Hint({ ok, label }: { ok: boolean; label: string }) {
  return <span className={ok ? 'text-ok' : 'text-mute'}>{ok ? '✓' : '○'} {label}</span>;
}
